/**
 * Servicio de autenticación simplificado
 * Soporta autenticación desde MentorAI App mediante token RSA encriptado
 */

import type { AuthPayload, DecryptedAuthData } from '../types';
import { decryptWithRSA } from '../utils/rsaDecryption';

export class AuthService {
  private readonly STORAGE_KEY = 'auth_user_data';

  /**
   * Extrae el token encriptado del parámetro auth= en la URL
   */
  private extractTokenFromURL(): string | null {
    if (typeof window === 'undefined') {
      return null;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const authToken = urlParams.get('auth');
    
    if (!authToken) {
      return null;
    }

    return authToken;
  }

  /**
   * Guarda los datos de autenticación en localStorage
   */
  private saveAuthToStorage(data: { email: string; user_id: string; full_name: string }): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
      // console.log('[AuthService] Datos de usuario guardados en localStorage');
    } catch (error) {
      // console.warn('[AuthService] No se pudo guardar en localStorage:', error);
    }
  }

  /**
   * Recupera los datos de autenticación desde localStorage
   */
  private loadAuthFromStorage(): { email: string; user_id: string; full_name: string } | null {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const data = JSON.parse(stored);
        if (data && data.email) {
          // console.log('[AuthService] Datos de usuario recuperados de localStorage:', data.email);
          return data;
        }
      }
    } catch (error) {
      // console.warn('[AuthService] Error al leer localStorage:', error);
    }
    return null;
  }

  /**
   * Desencripta y valida el token RSA
   */
  private decryptAndValidateToken(encryptedToken: string): DecryptedAuthData | null {
    try {
      // Desencriptar el token
      const decryptedJson = decryptWithRSA(encryptedToken);
      
      // Parsear el JSON
      const decryptedData: DecryptedAuthData = JSON.parse(decryptedJson);

      // Validar que tenga los campos necesarios
      if (!decryptedData.profile || !decryptedData.profile.email) {
        // console.error('[AuthService] Token desencriptado no contiene email');
        return null;
      }

      // Validar expiración del token
      const currentTime = Date.now();
      if (currentTime < decryptedData.created || currentTime > decryptedData.expire) {
        // console.error('[AuthService] Token expirado', {
        //   currentTime,
        //   created: decryptedData.created,
        //   expire: decryptedData.expire,
        // });
        return null;
      }

      return decryptedData;
    } catch (error) {
      // console.error('[AuthService] Error al desencriptar o validar token:', error);
      return null;
    }
  }

  /**
   * Valida el token con el servidor (opcional, similar a Unity)
   */
  private async validateTokenWithServer(token: string): Promise<boolean> {
    try {
      const endpoint = 'https://learnenglish.mentoraibot.com/api/v1/moodle/token_validation/';
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      });

      return response.ok;
    } catch (error) {
      // console.error('[AuthService] Error al validar token con servidor:', error);
      return false;
    }
  }

  /**
   * Procesa la autenticación desde la URL
   * Detecta el parámetro auth=, lo desencripta y retorna los datos del usuario
   */
  async processAuth(): Promise<AuthPayload | null> {
    try {
      // 1. Intentar extraer token de la URL
      const encryptedToken = this.extractTokenFromURL();
      
      if (encryptedToken) {
        // console.log('[AuthService] Token encontrado en URL, desencriptando...');

        // Desencriptar y validar token
        const decryptedData = this.decryptAndValidateToken(encryptedToken);
        
        if (decryptedData) {
          // console.log('[AuthService] Token desencriptado exitosamente:', {
          //   email: decryptedData.profile.email,
          //   userId: decryptedData.profile.id,
          // });

          // Validar con el servidor (opcional, puede fallar silenciosamente)
          try {
            const isValid = await this.validateTokenWithServer(decryptedData.token);
            if (!isValid) {
              // console.warn('[AuthService] Token no válido según el servidor');
            }
          } catch (error) {
            // console.warn('[AuthService] No se pudo validar con el servidor, continuando...');
          }

          const authPayload: AuthPayload = {
            token: decryptedData.token,
            user_id: decryptedData.profile.id,
            email: decryptedData.profile.email,
            full_name: decryptedData.profile.email,
          };

          // Guardar en localStorage para futuros accesos (refresh de página, etc.)
          this.saveAuthToStorage({
            email: decryptedData.profile.email,
            user_id: decryptedData.profile.id,
            full_name: decryptedData.profile.email,
          });

          // Limpiar el parámetro auth de la URL para que no quede visible
          try {
            const url = new URL(window.location.href);
            url.searchParams.delete('auth');
            window.history.replaceState({}, '', url.toString());
            // console.log('[AuthService] Parámetro auth eliminado de la URL');
          } catch (e) {
            // No es crítico si falla
          }

          return authPayload;
        } else {
          // console.warn('[AuthService] No se pudo desencriptar o validar el token');
        }
      }

      // 2. Si no hay token en URL (o falló), intentar recuperar de localStorage
      const storedData = this.loadAuthFromStorage();
      if (storedData) {
        // console.log('[AuthService] Usando datos guardados de localStorage:', storedData.email);
        return {
          email: storedData.email,
          user_id: storedData.user_id,
          full_name: storedData.full_name,
        };
      }

      // 3. No hay datos de autenticación disponibles
      // console.warn('[AuthService] No hay token en URL ni datos guardados en localStorage');
      return null;
    } catch (error) {
      // console.error('[AuthService] Error en processAuth:', error);
      return null;
    }
  }

  /**
   * Determinar si el usuario es docente
   */
  isDocente(roles?: string | null): boolean {
    return false;
  }

  /**
   * Determinar si el usuario es estudiante
   */
  isEstudiante(roles?: string | null): boolean {
    return true; // Por defecto es estudiante
  }

  /**
   * Obtener rol del usuario
   */
  getUserRole(): string | null {
    return null;
  }
}

export const authService = new AuthService();
export default authService;
